import React, { useContext, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { Form, Spinner } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../components/Button/Button'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { type GenerateTemplatePageState } from './GenerateTemplate'
import { SCHEDULING_EMAIL_TAB_KEYS, SCHEDULING_EMAIL_TEMPLATE_TYPE, TEMPLATE_TABS, TEMPLATE_TYPES } from './TemplatesTab'
import { useSchedulingTemplatesEnabled } from '../../Scheduling/shared/useSchedulingTemplatesEnabled'
import { type TemplateTabContent } from '../../../models/TemplateLibrary'
import { SchedulingEmailAudience } from '../../../models/Scheduling'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { dropdownSectionHeader } from '../../../components/SelectDropdown/dropdownOptions'
import { SectionDropdownItemOption } from '../../../components/SelectDropdown/SectionDropdownItemOption'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useCloneTemplateMutation } from '../../../store/templateLibrary/templateLibrary'
import { useAccount } from '../../../common/useAccount'
import { type SchedulingEmail } from './SchedulingEmails/schedulingEmails'
import { useSchedulingEmails } from './SchedulingEmails/useSchedulingEmails'
import './Templates.scss'

const CREATABLE_TEMPLATE_TYPES = TEMPLATE_TYPES.filter((t) => t.key !== 'your-templates')

const DEFAULT_TYPE = CREATABLE_TEMPLATE_TYPES.find((t) => t.key === 'meeting-summary') ?? CREATABLE_TEMPLATE_TYPES[0]

const SCHEDULING_EMAIL_GROUP_LABELS: Record<SchedulingEmailAudience, string> = {
  [SchedulingEmailAudience.CLIENTS]: 'Scheduling emails to clients',
  [SchedulingEmailAudience.ADVISOR]: 'Scheduling emails to team members',
}

const findMatchingType = (templateType: string, templateTypeId: string): TemplateTabContent | undefined =>
  CREATABLE_TEMPLATE_TYPES.find((t) => t.searchParams.type === templateType && (t.searchParams.typeId ?? t.key) === templateTypeId)

type TemplateCreationScope = 'account' | 'user'

const defaultName = (label: string) => `New ${label} template`

const buildTypeOptions = (schedulingEmails: SchedulingEmail[]): SelectOption[] =>
  TEMPLATE_TABS.flatMap((tab): SelectOption[] => {
    if ('audience' in tab) {
      const emails = schedulingEmails.filter((email) => email.audience === tab.audience)
      return emails.length > 0
        ? [dropdownSectionHeader(tab.key, SCHEDULING_EMAIL_GROUP_LABELS[tab.audience]), ...emails.map((email) => ({ id: email.slug, label: email.label }))]
        : []
    }
    if (!('searchParams' in tab)) {
      return SCHEDULING_EMAIL_TAB_KEYS.includes(tab.key) ? [] : [dropdownSectionHeader(tab.key, tab.label)]
    }
    return CREATABLE_TEMPLATE_TYPES.includes(tab) ? [{ id: tab.key, label: tab.label }] : []
  })

export const TemplateCreationPage: React.FC = () => {
  const { isEnabled: isSchedulingTemplateEnabled, isLoading: isSchedulingTemplateLoading } = useSchedulingTemplatesEnabled()
  const { hasScope } = useZocksAuth()
  const { account, isLoading: isAccountLoading } = useAccount()
  const hasTemplateAdminScope = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])
  const templatesLocked = account?.enterpriseConfig.lockTemplates ?? true
  const offersSchedulingEmails = isSchedulingTemplateEnabled && hasTemplateAdminScope && !templatesLocked
  const {
    emails: schedulingEmails,
    isLoading: isSchedulingEmailsLoading,
    isError: isSchedulingEmailsError,
    refetch: refetchSchedulingEmails,
  } = useSchedulingEmails({ skip: !offersSchedulingEmails })
  const incomingState = (useLocation().state as GenerateTemplatePageState) ?? {}
  const isRequestedSchedulingEmailMissing =
    offersSchedulingEmails &&
    incomingState.templateType === SCHEDULING_EMAIL_TEMPLATE_TYPE &&
    !schedulingEmails.some((email) => email.slug === incomingState.templateTypeId)

  if (hasTemplateAdminScope && (isSchedulingTemplateLoading || isAccountLoading || isSchedulingEmailsLoading)) {
    return (
      <CreationPageLayout>
        <Spinner />
      </CreationPageLayout>
    )
  }

  if (isSchedulingEmailsError) {
    return (
      <CreationPageLayout>
        <CreationPageNotice message='Could not load the scheduling emails. Please try again.' onRefresh={() => void refetchSchedulingEmails()} />
      </CreationPageLayout>
    )
  }

  if (isRequestedSchedulingEmailMissing) {
    return (
      <CreationPageLayout>
        <CreationPageNotice message='This scheduling email is not available.' />
      </CreationPageLayout>
    )
  }

  return (
    <CreationPageLayout>
      <TemplateCreationForm schedulingEmails={schedulingEmails} hasTemplateAdminScope={hasTemplateAdminScope} />
    </CreationPageLayout>
  )
}

const CreationPageLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const { portalElement } = useContext(HeaderContext)

  return (
    <>
      {portalElement
        ? createPortal(
            <div className='d-flex align-items-center zocks-pl-2.0'>
              <Button variant='link' className='back-button' onClick={() => navigate(-1)}>
                <i className='bi bi-chevron-left' />
              </Button>
            </div>,
            portalElement,
          )
        : null}

      <div className='d-flex flex-column align-items-center overflow-y-auto w-100 pt-5'>{children}</div>
    </>
  )
}

interface CreationPageNoticeProps {
  message: string
  onRefresh?: () => void
}

const CreationPageNotice: React.FC<CreationPageNoticeProps> = ({ message, onRefresh }) => {
  const navigate = useNavigate()

  return (
    <div className='d-flex flex-column align-items-center gap-3 w-100'>
      <div className='paragraph-medium zocks-text-gray-700 text-center'>{message}</div>
      <div className='d-flex gap-2'>
        <Button variant='secondary' onClick={() => navigate(-1)}>
          Cancel
        </Button>
        {onRefresh ? (
          <Button variant='primary' onClick={onRefresh}>
            Refresh
          </Button>
        ) : null}
      </div>
    </div>
  )
}

interface TemplateCreationFormProps {
  schedulingEmails: SchedulingEmail[]
  hasTemplateAdminScope: boolean
}

const TemplateCreationForm: React.FC<TemplateCreationFormProps> = ({ schedulingEmails, hasTemplateAdminScope }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const accountId = useAppSelector(selectAccountId)
  const [cloneTemplate, { isLoading: isCloning }] = useCloneTemplateMutation()

  const incomingState = (location.state as GenerateTemplatePageState) ?? {}
  const initialSchedulingEmail =
    incomingState.templateType === SCHEDULING_EMAIL_TEMPLATE_TYPE ? schedulingEmails.find((email) => email.slug === incomingState.templateTypeId) : undefined
  const initialType = findMatchingType(incomingState.templateType ?? '', incomingState.templateTypeId ?? '') ?? DEFAULT_TYPE
  const initialSelection = initialSchedulingEmail ?? initialType

  const [selectedTypeKey, setSelectedTypeKey] = useState(initialSchedulingEmail?.slug ?? initialType.key)
  const [name, setName] = useState(defaultName(initialSelection.label))
  const [description, setDescription] = useState('')
  const [scope, setScope] = useState<TemplateCreationScope>(hasTemplateAdminScope ? 'account' : 'user')

  const selectedSchedulingEmail = schedulingEmails.find((email) => email.slug === selectedTypeKey)
  const selectedType = CREATABLE_TEMPLATE_TYPES.find((t) => t.key === selectedTypeKey) ?? DEFAULT_TYPE
  const canCreate = name.trim() !== '' && !isCloning

  const typeOptions = useMemo(() => buildTypeOptions(schedulingEmails), [schedulingEmails])
  const selectedTypeOption = useMemo(() => typeOptions.find((o) => o.id === selectedTypeKey) ?? null, [typeOptions, selectedTypeKey])

  const handleTypeOptionSelected = (option: SelectOption) => {
    const email = schedulingEmails.find((candidate) => candidate.slug === option.id)
    const type = CREATABLE_TEMPLATE_TYPES.find((t) => t.key === option.id)
    const label = email?.label ?? type?.label
    if (label == null) {
      return
    }
    setSelectedTypeKey(option.id)
    setName(defaultName(label))
  }

  const createSchedulingEmailTemplate = async (email: SchedulingEmail) => {
    const clone = await cloneTemplate({
      accountId,
      templateId: email.defaultTemplateId,
      request: { name: name.trim(), description: description.trim() || undefined, scope: 'account' },
    }).unwrap()
    navigate(`/workspace/setup/templates/template/${clone.id}`, { replace: true })
  }

  const handleCreateTemplate = () => {
    if (!canCreate) {
      return
    }
    if (selectedSchedulingEmail) {
      void createSchedulingEmailTemplate(selectedSchedulingEmail)
      return
    }
    const state: GenerateTemplatePageState = {
      templateTypeName: selectedType.label,
      templateType: selectedType.searchParams.type ?? '',
      templateTypeId: selectedType.searchParams.typeId ?? selectedType.key,
      templateName: name.trim(),
      templateDescription: description.trim() || undefined,
      templateScope: scope,
    }
    navigate('/workspace/setup/templates/template/create/generate', { state, replace: true })
  }

  return (
    <div className='d-flex flex-column gap-4 w-100 choose-template-creation-form'>
      <h5 className='fw-bold text-center mb-0'>Create new template</h5>
      <div className='d-flex flex-column gap-4'>
        <Form.Group>
          <Form.Label>Type</Form.Label>
          <SelectDropdown
            options={typeOptions}
            initialSelectedOption={selectedTypeOption}
            onOptionSelected={handleTypeOptionSelected}
            DropDownItemOptionComponent={SectionDropdownItemOption}
            className='choose-template-type-select'
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>Name</Form.Label>
          <Form.Control type='text' value={name} onChange={(e) => setName(e.target.value)} />
        </Form.Group>

        <Form.Group>
          <Form.Label>Description</Form.Label>
          <Form.Control
            as='textarea'
            rows={3}
            placeholder='Briefly describe what this template is for'
            value={description}
            className={'template-description-textarea'}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>Access</Form.Label>
          <AccessField
            isSchedulingEmail={selectedSchedulingEmail != null}
            hasTemplateAdminScope={hasTemplateAdminScope}
            scope={scope}
            onScopeChange={setScope}
          />
        </Form.Group>
      </div>

      <div className='d-flex gap-2 justify-content-end'>
        <Button variant='secondary' onClick={() => navigate(-1)} disabled={isCloning}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleCreateTemplate} disabled={!canCreate}>
          Create template
        </Button>
      </div>
    </div>
  )
}

interface FixedAccessProps {
  icon: string
  label: string
  note: string
}

const FixedAccess: React.FC<FixedAccessProps> = ({ icon, label, note }) => (
  <div className='d-flex gap-2 align-items-center'>
    <i className={classNames('bi flex-shrink-0', icon)} />
    <span>
      {label} <span className='zocks-text-gray-600'>({note})</span>
    </span>
  </div>
)

interface AccessFieldProps {
  isSchedulingEmail: boolean
  hasTemplateAdminScope: boolean
  scope: TemplateCreationScope
  onScopeChange: (scope: TemplateCreationScope) => void
}

const AccessField: React.FC<AccessFieldProps> = ({ isSchedulingEmail, hasTemplateAdminScope, scope, onScopeChange }) => {
  if (isSchedulingEmail) {
    return <FixedAccess icon='bi-people-fill' label='Account' note='anyone in your account' />
  }
  if (!hasTemplateAdminScope) {
    return <FixedAccess icon='bi-person-fill' label='Personal' note='only you' />
  }
  return (
    <div className='d-flex flex-column gap-2'>
      <Form.Check
        type='radio'
        id='scope-account'
        name='scope'
        checked={scope === 'account'}
        onChange={() => onScopeChange('account')}
        label={
          <span>
            <i className='bi bi-people-fill me-1' />
            Account <span className='zocks-text-gray-600'>(anyone in your account)</span>
          </span>
        }
      />
      <Form.Check
        type='radio'
        id='scope-user'
        name='scope'
        checked={scope === 'user'}
        onChange={() => onScopeChange('user')}
        label={
          <span>
            <i className='bi bi-person-fill me-1' />
            Personal <span className='zocks-text-gray-600'>(only you)</span>
          </span>
        }
      />
    </div>
  )
}
